import React, { useState, useRef, useEffect } from 'react'
import { ArrowUp, Paperclip, Mic } from 'lucide-react'

interface ChatInputProps {
  onSendMessage: (message: string) => void
  disabled?: boolean
}

/**
 * 聊天输入框组件 - 2025年现代ChatGPT风格
 * 极简设计，自适应高度，现代化交互
 */
const ChatInput: React.FC<ChatInputProps> = ({ onSendMessage, disabled }) => {
  const [inputValue, setInputValue] = useState('')
  const [isComposing, setIsComposing] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  // 自动调整textarea高度
  useEffect(() => {
    const textarea = textareaRef.current
    if (textarea) {
      textarea.style.height = 'auto'
      textarea.style.height = Math.min(textarea.scrollHeight, 200) + 'px'
    }
  }, [inputValue])

  const handleSendMessage = () => {
    if (!inputValue.trim() || disabled || isComposing) return

    onSendMessage(inputValue)
    setInputValue('')
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // 检查是否在输入法组合键输入过程中
    if (
      e.nativeEvent.isComposing ||
      e.nativeEvent.keyCode === 229 ||
      isComposing
    ) {
      return // 在输入法组合键输入过程中，不处理Enter键
    }

    if (e.key === 'Enter') {
      if (e.shiftKey) {
        // Shift + Enter 换行，不做任何处理，让默认行为发生
        return
      } else {
        // 单独按 Enter 发送消息
        e.preventDefault()
        handleSendMessage()
      }
    }
  }

  const handleCompositionStart = () => {
    setIsComposing(true)
  }

  const handleCompositionEnd = () => {
    setIsComposing(false)
  }

  return (
    <div className="border-gray-100 bg-white">
      <div className="max-w-4xl mx-auto px-6 py-4">
        <div className="relative">
          {/* 主输入区域 */}
          <div className="relative flex items-end bg-white border border-gray-200 rounded-3xl shadow-sm hover:shadow-md transition-shadow duration-200 focus-within:border-gray-300">
            {/* 附件按钮 */}
            <button
              className="flex-shrink-0 p-3 text-gray-400 hover:text-gray-600 transition-colors"
              disabled={disabled}
              title="附件"
            >
              <Paperclip size={20} />
            </button>

            {/* 文本输入区域 */}
            <textarea
              ref={textareaRef}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              onCompositionStart={handleCompositionStart}
              onCompositionEnd={handleCompositionEnd}
              placeholder="给AI发消息..."
              disabled={disabled}
              rows={1}
              className="flex-1 resize-none border-0 bg-transparent py-3 text-base placeholder-gray-500 focus:outline-none min-h-[24px] max-h-[200px] overflow-y-auto"
              style={{
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
              }}
            />

            {/* 语音/发送按钮 */}
            <div className="flex-shrink-0 p-2">
              {inputValue.trim() ? (
                <button
                  onClick={handleSendMessage}
                  disabled={disabled}
                  className="flex items-center justify-center w-8 h-8 text-white disabled:bg-gray-300 disabled:cursor-not-allowed transition-all duration-200 hover:shadow-lg disabled:hover:shadow-none"
                  style={{
                    borderRadius: '31px',
                    background: disabled
                      ? '#d1d5db'
                      : 'linear-gradient(270deg, #5239E0 0.29%, #9684FF 101.29%)',
                  }}
                  title="发送"
                >
                  <ArrowUp size={16} />
                </button>
              ) : (
                <button
                  className="flex items-center justify-center w-8 h-8 text-gray-400 hover:text-gray-600 transition-colors rounded-full"
                  disabled={disabled}
                  title="语音"
                >
                  <Mic size={16} />
                </button>
              )}
            </div>
          </div>

          {/* 底部提示 */}
          <div className="mt-2 text-xs text-gray-500 text-center">
            AI可能会犯错。请核实重要信息。
          </div>
        </div>
      </div>
    </div>
  )
}

export default ChatInput
